import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons/faEllipsisVertical';
import { QMenu, QMenuItem, QIconButton, QDivider, SxProps } from '@captivateiq/quotient';
import React, { useState } from 'react';

export interface VisualizationAction<T = any> {
  displayName: string;
  icon?: IconDefinition;
  onClick?: (item: T) => void;
  type?: 'regular' | 'destructive';
  menuGroup?: string;
  isDisabled?: boolean;
}

export interface VisualizationActionsMenuProps<T = any> {
  item: T;
  actions: VisualizationAction<T>[];
  icon?: IconDefinition;
  sx?: SxProps;
  variant?: 'ghost' | 'solid' | 'outline';
}

const DEFAULT_GROUPING = 'default';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const VisualizationActionsMenu = <T extends any>({
  item,
  actions,
  icon = faEllipsisVertical,
  sx,
  variant = 'ghost',
}: VisualizationActionsMenuProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent any parent click handlers
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action: VisualizationAction<T>) => {
    if (action.onClick) {
      action.onClick(item);
    }
    handleClose();
  };

  // Group actions by menuGroup
  const groupedActions = actions.reduce((groups, action) => {
    const group = action.menuGroup || DEFAULT_GROUPING;
    if (!groups[group]) {
      // eslint-disable-next-line no-param-reassign
      groups[group] = [];
    }
    groups[group].push(action);
    return groups;
  }, {} as Record<string, VisualizationAction<T>[]>);

  const groupKeys = Object.keys(groupedActions);

  return (
    <>
      <QIconButton
        data-testid="visualization-actions-menu-button"
        icon={icon}
        size="small"
        sx={{
          color: 'text.secondary',
          '&:hover': {
            color: 'text.primary',
          },
          ...sx,
        }}
        variant={variant}
        onClick={handleClick}
      />

      <QMenu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={open}
        PaperProps={{
          sx: {
            minWidth: 120,
            boxShadow: 3,
          },
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handleClose}
      >
        {groupKeys.map((groupKey, groupIndex) => (
          <React.Fragment key={groupKey}>
            {groupedActions[groupKey].map((action) => (
              <QMenuItem
                data-testid={`action-${action.displayName.toLowerCase().replace(/\s+/g, '-')}`}
                destructive={action.type === 'destructive'}
                disabled={action.isDisabled}
                key={action.displayName}
                leftIcon={action.icon}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  ...(action.type === 'destructive' && {
                    color: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.light',
                      color: 'error.contrastText',
                    },
                  }),
                }}
                onClick={() => handleActionClick(action)}
              >
                {action.displayName}
              </QMenuItem>
            ))}
            {/* Add divider between groups (except for last group) */}
            {groupIndex < groupKeys.length - 1 && <QDivider />}
          </React.Fragment>
        ))}
      </QMenu>
    </>
  );
};
